import { useState } from 'react';
import { useTheme } from '../theme/ThemeProvider';
import type { BrandDocument } from './brand';

/**
 * Which way a caller's background moves with the theme, so the hook knows
 * which of `logoLight`/`logoDark` actually reads on it:
 *
 * - `'page'` — the surface follows the theme directly (light theme → light
 *   surface, dark theme → dark surface). This is the ordinary case: `bg-cream`
 *   on the sidebar, the intake section header, `BrandLockup`'s callers.
 * - `'inverted'` — the surface runs the OPPOSITE direction from the page
 *   theme. The intake intro card is `bg-navy`, and `navy` is dark in light
 *   mode but flips light in dark mode (src/index.css) — so in LIGHT theme
 *   the card itself renders DARK (wants `logoDark`), and in DARK theme the
 *   card renders LIGHT (wants `logoLight`). Exactly backwards from `'page'`.
 *
 * There is no default: a hard-coded default would be silently wrong for
 * whichever polarity it didn't cover, which is exactly the bug this hook
 * exists to prevent. Every caller states which surface it's on.
 */
export type LogoSurface = 'page' | 'inverted';

/**
 * The image-vs-text decision shared by every lockup surface (`BrandLockup`, the
 * sidebar wordmark, the intake section header, the intake intro card): which of
 * `logoLight`/`logoDark` (see the naming-convention doc comment on
 * `BrandDocument`) matches the CURRENT resolved theme ON THIS CALLER'S SURFACE
 * (see `LogoSurface`), plus the fail-soft bookkeeping an `<img>` caller needs:
 * a failure is remembered for the source that failed, so when the candidate
 * source changes (brand swap, a theme toggle that switches to the OTHER variant)
 * a past failure does not suppress a different, perfectly good image. One source
 * is remembered, the latest to fail: a source that failed is not tried again when
 * the candidate changes away and back (the effect it replaces did retry it), unless
 * another source has failed in between.
 *
 * The failed SOURCE is kept, not a flag that an effect resets on a source change
 * (crossmarc/steward-app#2194). An effect runs some time after the image is in
 * the document, so an `error` arriving in that gap was undone by the reset and
 * the broken image stayed. Seen in tests under CPU load, 2026-10-08; whether a
 * browser can deliver `error` in that gap was not checked.
 *
 * Callers keep their own markup/sizing/collapsed-state handling; this hook only
 * owns the decision, not the rendering. `src: null` covers both "brand has no
 * asset for this theme" and "it failed to load" — a caller doesn't need to tell
 * those apart, both mean "render your existing text treatment instead."
 */
export function useBrandLogo(doc: BrandDocument | null | undefined, surface: LogoSurface) {
  const { theme } = useTheme(); // already resolves 'system' to 'light' | 'dark'
  // On 'page', dark theme means the surface itself is dark, so it wants the
  // dark-surface asset. On 'inverted', that relationship flips: light theme is
  // when the (inverted) surface renders dark, so light theme wants logoDark.
  const surfaceIsDark = surface === 'page' ? theme === 'dark' : theme === 'light';
  const logoSrc = surfaceIsDark ? doc?.logoDark : doc?.logoLight;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc !== null && failedSrc === logoSrc;

  return {
    /** The URL to render, or `null` when the caller should fall back to text. */
    src: logoSrc && !failed ? logoSrc : null,
    /** Wire directly to the `<img>`'s `onError`. */
    onError: () => setFailedSrc(logoSrc ?? null),
  };
}
